// libs
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { useHistory, useLocation, useParams } from 'react-router';
import useTheme from '@nykaa/ui-components/themes/hooks/useTheme';
import { useSelector } from 'react-redux';
import IOElement from 'shared/components/IOElement';

// icons
import ArrowRight from '@nykaa/ui-components/Icons/arrow-right';
import ChevronRight from '@nykaa/ui-components/Icons/chevron-right-small';
import ReviewList from 'shared/layouts/ReviewList/desktop';

// selectors
import {
  getDesignCode,
  getProductId,
  getReviewSummary,
  getBrandBook,
} from 'shared/store/details/selectors';

// styles
import {
  BasedOnDark,
  Button,
  Heading,
  Heading2,
  OffsetForScroll,
  RatingCount,
  SummaryContainer,
  Wrapper,
  BrandbookHelperText,
} from './styles';

// components
import ReviewCard, { REVIEW_DETAILS_SOURCES } from 'shared/components/ReviewCard';
import StarRating from 'shared/components/StarRating';
import ReviewAllImages, { IMAGE_VIEW_TYPE } from 'shared/components/ReviewAllImages';

// constants
import { ALL_REVIEW } from 'shared/constants/uri';
import { RENDER_MODE } from 'shared/constants';

// helpers
import { getValidDate } from './helpers';
import { getDeviceType } from 'shared/components/ResponsiveLoader/helpers';
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

// types
import { Theme } from '@nykaa/ui-components/themes/types';

const TEXT = {
  HEADING: 'Customer Reviews',
  USEFUL_REVIEW: 'Most useful review',
  VIEW_ALL: 'Read all reviews',
};

interface BrandBookItem {
  widget_type: string;
  // You can add more properties depending on your data structure
  items: any[];
  title?: string;
}

const MAX_RATING_COUNT = 5;
const basedOn = (count: number) => `Based on ${count} ratings`;

const SHOW_REVIEWS = 'all-reviews';

function CustomerReview() {
  const review = useSelector(getReviewSummary);
  const productId = useSelector(getProductId);
  const designCode = useSelector(getDesignCode);
  const brandBookData: BrandBookItem[] = useSelector(getBrandBook);
  // Check if review_tags_widget is present
  const isReviewTagsWidgetPresent = brandBookData.find(
    (item: BrandBookItem) => item.widget_type === 'review_tags_widget'
  );
  // Set the helper text based on the presence of review_tags_widget
  const BRANDBOOK_HELPER_TEXT = isReviewTagsWidgetPresent
    ? 'Eyeing this product but missing reviews? See what customers are saying about this brand below before making a decision.'
    : 'Eyeing this product but missing reviews? Know more about the brand below before making a decision.';

  const { slug, id } = useParams<{ slug: string; id: string }>();
  const isMobile = getDeviceType() === RENDER_MODE.MOBILE;
  const { pathname, search } = useLocation();
  const history = useHistory();
  const theme: Theme = useTheme();
  const [shouldShowAllReview, setShouldShowAllReview] = useState<boolean>(false);
  const topReview: Review.ITopReview = get(review, 'top_reviews.0', {});
  const reviewCount = get(review, 'review_count', 0);
  const averageRating = get(review, 'star_rating', 0);
  const starRatingCount = get(review, 'star_rating_count', 0);
  const shouldShowRatings = get(review, 'show_ratings', false);

  const [reviewVisible, setReviewVisible] = useState(false);
  const isDwebRatingReviewsEnabled = get(useRemoteConfig('ratingReviewEnabled'), 'desktop', false);
  const isMwebRatingReviewsEnabled = get(useRemoteConfig('ratingReviewEnabled'), 'mobile', false);

  const actionUrl = isMobile
    ? `/${slug}/p/${id}${ALL_REVIEW}${designCode ? '?designCode=' + designCode : ''}`
    : `/${slug}/p/${id}?all-reviews=1${designCode ? '&designCode=' + designCode : ''}`;

  const data = useMemo(
    () => ({
      ...topReview,
      description: topReview.detail,
      name: topReview.nickname,
      isBuyer: topReview.is_buyer,
      likeCount: get(review, 'top_reviews.0.likes', ''),
      id: get(review, 'top_reviews.0.review_id', ''),
      isLikedByUser: get(review, 'top_reviews.0.isLikedByUser', false),
      reviewCreationText: getValidDate(topReview.created_at),
    }),
    [review, topReview]
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(search);

    !isMobile && setShouldShowAllReview(searchParams.get(SHOW_REVIEWS) === '1');
  }, [isMobile, search]);

  const onHide = useCallback(() => {
    const params = new URLSearchParams(search);
    params.delete(SHOW_REVIEWS);
    let url = pathname + '?' + params.toString();
    history.replace(url);
    setShouldShowAllReview(false);
  }, [history, pathname, search]);

  if (isEmpty(topReview) || !shouldShowRatings) {
    return null;
  }

  if ((!isDwebRatingReviewsEnabled && !isMobile) || (isMobile && !isMwebRatingReviewsEnabled))
    return null;

  return (
    <Wrapper>
      <IOElement onVisible={() => setReviewVisible(true)} once />
      <OffsetForScroll id="customer-review-section" />
      <Heading data-at="customer-reviews">{TEXT.HEADING}</Heading>

      {averageRating && (
        <SummaryContainer>
          <StarRating
            activeCount={averageRating}
            size="24px"
            activeColor={theme.colors.textPrimary}
            data-test="star-rating-wrapper"
            className="star-rating-container"
          />
          <RatingCount data-at="product-rating">
            {Number(averageRating).toFixed(1)}
            <span className="maxCount">/{MAX_RATING_COUNT}</span>
          </RatingCount>

          {starRatingCount && (
            <BasedOnDark data-test="basedOn-dark">{basedOn(starRatingCount)}</BasedOnDark>
          )}
        </SummaryContainer>
      )}

      {reviewVisible && (
        <ReviewAllImages
          isMobile={isMobile}
          viewType={IMAGE_VIEW_TYPE.RAIL}
          source={REVIEW_DETAILS_SOURCES.IMAGE_RAIL_PDP_SECTION}
        />
      )}

      {topReview && (
        <>
          <Heading2>{TEXT.USEFUL_REVIEW}</Heading2>
          <ReviewCard
            isMobile={isMobile}
            data-test="top-review"
            review={data}
            productId={productId}
            source={REVIEW_DETAILS_SOURCES.PDP_TOP_REVIEW_SECTION}
            showHelpful={false}
          />
        </>
      )}

      {reviewCount > 1 && (
        <Button to={actionUrl} data-test="allReviewCTA" data-at="read-reviews-btn">
          <span>{TEXT.VIEW_ALL}</span>
          {isMobile ? <ArrowRight size="20px" /> : <ChevronRight />}
        </Button>
      )}
      {shouldShowAllReview && !isMobile && <ReviewList onhide={onHide} />}
      {reviewCount < 1 && brandBookData.length > 0 && (
        <BrandbookHelperText>{BRANDBOOK_HELPER_TEXT}</BrandbookHelperText>
      )}
    </Wrapper>
  );
}

export default memo(CustomerReview);
